import { Head, Link, router } from "@inertiajs/react"
import { Contact } from "lucide-react"
import { useState } from "react"

import { HpiDataTable, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface Customer {
  id: number
  display_name: string
  company_name: string | null
  city: string | null
  state: string | null
  phone: string | null
  email: string | null
  client_type: string | null
  sales_rep: string | null
  status: number
  updated_at: string
  // Case count stats (case_counts mode)
  active_cases?: number
  closed_cases?: number
  referred_cases?: number
  // Marketing stats (marketing_stats / marketing_all_stats mode)
  cases_3mo?: number
  cases_6mo?: number
  cases_1yr?: number
  // Extended marketing stats (marketing_all_stats mode)
  ref_6mo_chg?: number
  ref_y1?: number
  ref_y2?: number
  ref_y3?: number
}

interface SelectOption {
  value: string
  label: string
}

interface FilterOptions {
  types: SelectOption[]
  categories: SelectOption[]
  statuses: SelectOption[]
  reps: SelectOption[]
  lead_sources: SelectOption[]
  states: SelectOption[]
  sorts: SelectOption[]
  history: SelectOption[]
}

interface Filters {
  name?: string
  city?: string
  state?: string
  phone?: string
  email?: string
  type?: string
  category?: string
  status?: string
  lead_source?: string
  rep?: string
  sort?: string
  history?: string
}

type StatsMode =
  | "marketing_stats"
  | "marketing_all_stats"
  | "case_counts"
  | "none"

interface StatsYears {
  y1: number
  y2: number
  y3: number
}

interface Props {
  customers: Customer[]
  stats_mode: StatsMode
  stats_years: StatsYears | null
  filters: Filters
  filter_options: FilterOptions
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Customers", href: "/customers" },
]

function formatDate(date: string): string {
  return formatDateUtil(date, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

const PER_PAGE = 20

const BASE_COLUMNS: HpiColumn<Customer>[] = [
  {
    header: "Date of Last",
    subHeader: "Activity",
    width: "max-w-[120px]",
    render: (row) => (
      <div className="text-sm">{formatDate(row.updated_at)}</div>
    ),
  },
  {
    header: "Customer",
    subHeader: "City, State",
    render: (row) => (
      <div>
        <Link
          href={`/customers/${row.id}`}
          className="text-hpi-dark-gray hover:text-hpi-active-blue font-semibold"
        >
          {row.display_name}
        </Link>
        {[row.city, row.state].some(Boolean) && (
          <div className="text-hpi-med-blue text-sm">
            {[row.city, row.state].filter(Boolean).join(", ")}
          </div>
        )}
      </div>
    ),
  },
  {
    header: "Rep",
    width: "max-w-[120px]",
    render: (row) => <div>{row.sales_rep ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="break-words">{row.client_type ?? "-"}</div>
    ),
  },
]

function getStatsColumns(
  mode: StatsMode,
  years: StatsYears | null,
): HpiColumn<Customer>[] {
  if (mode === "marketing_stats" || mode === "marketing_all_stats") {
    const cols: HpiColumn<Customer>[] = [
      {
        header: "Case/Ref",
        subHeader: "3mo",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.cases_3mo ?? 0) > 0 ? row.cases_3mo : ""}</div>
        ),
      },
      {
        header: "Case/Ref",
        subHeader: "6mo",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.cases_6mo ?? 0) > 0 ? row.cases_6mo : ""}</div>
        ),
      },
      {
        header: "Case/Ref",
        subHeader: "1yr",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.cases_1yr ?? 0) > 0 ? row.cases_1yr : ""}</div>
        ),
      },
    ]
    if (mode === "marketing_all_stats" && years) {
      cols.push(
        {
          header: "Case/Ref",
          subHeader: "6mo Chg",
          width: "max-w-[70px]",
          align: "center",
          hideOnMobile: true,
          cellClassName: "bg-hpi-yellow/20",
          render: (row) => {
            const val = row.ref_6mo_chg ?? 0
            if (val === 0) return <div />
            if (val > 0)
              return <div className="font-medium text-green-600">+{val}%</div>
            return <div className="font-medium text-red-600">{val}%</div>
          },
        },
        {
          header: "Case/Ref",
          subHeader: `${years.y1}`,
          width: "max-w-[70px]",
          align: "center",
          hideOnMobile: true,
          cellClassName: "bg-hpi-yellow/20",
          render: (row) => <div>{(row.ref_y1 ?? 0) > 0 ? row.ref_y1 : ""}</div>,
        },
        {
          header: "Case/Ref",
          subHeader: `${years.y2}`,
          width: "max-w-[70px]",
          align: "center",
          hideOnMobile: true,
          cellClassName: "bg-hpi-yellow/20",
          render: (row) => <div>{(row.ref_y2 ?? 0) > 0 ? row.ref_y2 : ""}</div>,
        },
        {
          header: "Case/Ref",
          subHeader: `${years.y3}`,
          width: "max-w-[70px]",
          align: "center",
          hideOnMobile: true,
          cellClassName: "bg-hpi-yellow/20",
          render: (row) => <div>{(row.ref_y3 ?? 0) > 0 ? row.ref_y3 : ""}</div>,
        },
      )
    }
    return cols
  }

  if (mode === "case_counts") {
    return [
      {
        header: "Active",
        subHeader: "Cases",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.active_cases ?? 0) > 0 ? row.active_cases : ""}</div>
        ),
      },
      {
        header: "Closed",
        subHeader: "Cases",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.closed_cases ?? 0) > 0 ? row.closed_cases : ""}</div>
        ),
      },
      {
        header: "Referred",
        subHeader: "Cases",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => (
          <div>{(row.referred_cases ?? 0) > 0 ? row.referred_cases : ""}</div>
        ),
      },
      {
        header: "Total",
        subHeader: "Cases",
        width: "max-w-[70px]",
        align: "center",
        hideOnMobile: true,
        render: (row) => {
          const total =
            (row.active_cases ?? 0) +
            (row.closed_cases ?? 0) +
            (row.referred_cases ?? 0)
          return <div>{total > 0 ? total : ""}</div>
        },
      },
    ]
  }

  return []
}

// Which filters use a select dropdown vs text input
const SELECT_FILTERS = [
  "state",
  "type",
  "category",
  "status",
  "lead_source",
  "rep",
  "history",
  "sort",
] as const
type SelectFilterKey = (typeof SELECT_FILTERS)[number]

function isSelectFilter(key: string): key is SelectFilterKey {
  return (SELECT_FILTERS as readonly string[]).includes(key)
}

function getOptionsForFilter(
  key: SelectFilterKey,
  filterOptions: FilterOptions,
): SelectOption[] {
  switch (key) {
    case "state":
      return filterOptions.states
    case "type":
      return filterOptions.types
    case "category":
      return filterOptions.categories
    case "status":
      return filterOptions.statuses
    case "lead_source":
      return filterOptions.lead_sources
    case "rep":
      return filterOptions.reps
    case "history":
      return filterOptions.history
    case "sort":
      return filterOptions.sorts
  }
}

const FILTER_FIELDS: { key: string; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "type", label: "Type" },
  { key: "category", label: "Category" },
  { key: "status", label: "Status" },
  { key: "lead_source", label: "Lead Source" },
  { key: "rep", label: "Rep" },
  { key: "history", label: "History" },
  { key: "sort", label: "Sort" },
]

export default function Index({
  customers,
  stats_mode,
  stats_years,
  filters,
  filter_options,
}: Props) {
  const [page, setPage] = useState(1)
  const [localFilters, setLocalFilters] = useState<Filters>(filters)
  const isFiltering = Object.entries(filters).some(
    ([key, v]) => v && key !== "sort",
  )

  const totalPages = Math.ceil(customers.length / PER_PAGE)
  const paginated = customers.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleSearch = () => {
    const params: Record<string, string> = {}
    for (const [key, value] of Object.entries(localFilters) as [
      string,
      string | undefined,
    ][]) {
      if (value) params[key] = value
    }
    router.get("/customers", params, { preserveState: false })
  }

  const handleReset = () => {
    router.get("/customers", {}, { preserveState: false })
  }

  const updateFilter = (key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }))
  }

  // Build search description badges
  const searchBadges: { label: string; value: string }[] = []
  for (const field of FILTER_FIELDS) {
    const val = filters[field.key as keyof Filters]
    if (!val || field.key === "sort") continue
    // For select filters, find the label
    if (isSelectFilter(field.key)) {
      const opts = getOptionsForFilter(field.key, filter_options)
      const opt = opts.find((o) => o.value === val)
      searchBadges.push({ label: field.label, value: opt?.label ?? val })
    } else {
      searchBadges.push({ label: field.label, value: val })
    }
  }

  const SORT_TITLES: Record<string, string> = {
    activeCases: "Most Active Cases",
    cases12mo: "Most Cases - Past 12 mo.",
    cases24mo: "Most Cases - Past 24 mo.",
    totalCases: "Total Cases",
  }

  const title = isFiltering
    ? "Search Results"
    : (SORT_TITLES[filters.sort ?? ""] ?? "Recent Activity")

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Customers" />

      <div className="p-6">
        {/* Filter Bar */}
        <div className="mb-4 flex flex-wrap items-end gap-3">
          {FILTER_FIELDS.map((field) => (
            <div key={field.key} className="flex flex-col gap-1">
              <label className="text-hpi-med-gray text-xs font-medium">
                {field.label}
              </label>
              {isSelectFilter(field.key) ? (
                <select
                  value={localFilters[field.key as keyof Filters] ?? ""}
                  onChange={(e) => updateFilter(field.key, e.target.value)}
                  className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 rounded border bg-white pr-7 pl-2 text-sm focus:ring-1"
                >
                  <option value="">
                    {field.key === "sort" ? "Default" : "Any"}
                  </option>
                  {getOptionsForFilter(field.key, filter_options).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={localFilters[field.key as keyof Filters] ?? ""}
                  onChange={(e) => updateFilter(field.key, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch()
                  }}
                  className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 w-24 rounded border bg-white px-2 text-sm focus:ring-1"
                />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="bg-hpi-dark-blue hover:bg-hpi-blue h-8 rounded px-5 text-sm font-medium text-white transition-colors"
            >
              Search
            </button>
            {isFiltering && (
              <button
                onClick={handleReset}
                className="border-hpi-med-gray text-hpi-dark-gray hover:bg-hpi-pale-gray h-8 rounded border px-3 text-sm transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Search description badges */}
        {searchBadges.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {searchBadges.map((badge, i) => (
              <span
                key={i}
                className="bg-hpi-dark-blue inline-block rounded px-2 py-0.5 text-xs text-white"
              >
                {badge.label}: {badge.value}
              </span>
            ))}
          </div>
        )}

        <HpiDataTable<Customer>
          title={title}
          actions={[
            {
              label: "New",
              icon: Contact,
              onClick: () => {
                window.location.href = "/customers/new"
              },
            },
          ]}
          columns={[
            ...BASE_COLUMNS,
            ...getStatsColumns(stats_mode, stats_years),
          ]}
          data={paginated}
          keyFn={(row) => row.id}
          pagination={
            customers.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={customers.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {customers.length === 0 && isFiltering && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 p-4 text-red-700">
            No customers found matching your search criteria.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
